/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */



import React, { useState, useEffect } from 'react';

const ColumnOrderModal = ({ theme, table, onClose, onSave, apiHelper, applicationId }) => {
    const [fields, setFields] = useState([]);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (table && table.fields) {
            const sortedFields = [...table.fields].sort((a, b) => a.display_order - b.display_order);
            setFields(sortedFields);
        }
    }, [table]);

    if (!table) return null;

    const onDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOver = (index) => {
        if (draggedItemIndex === null || draggedItemIndex === index) return;

        const newFields = [...fields];
        const draggedItem = newFields[draggedItemIndex];

        newFields.splice(draggedItemIndex, 1);
        newFields.splice(index, 0, draggedItem);

        setDraggedItemIndex(index);
        setFields(newFields);
    };

    const onDragEnd = () => {
        setDraggedItemIndex(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const fieldOrder = fields.map(f => f.id || f.name);

            const payload = {
                application_id: applicationId,
                table_name: table.name,
                field_order: fields.map(f => f.id)
            };

            const response = await apiHelper.apiCall('/arcee/application/table/reorder-fields/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                onSave();
                onClose();
            } else {
                const err = await response.json();
                alert(err.error || "Failed to reorder fields");
            }
        } catch (error) {
            console.error("Error reordering fields:", error);
            alert("An error occurred");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 3001,
            display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
        }} onClick={onClose}>
            <div style={{
                background: theme.footer, borderRadius: theme.cardRadius,
                width: '100%', maxWidth: '600px', maxHeight: '80vh',
                display: 'flex', flexDirection: 'column',
                padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                color: theme.text,
            }} onClick={e => e.stopPropagation()}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: 0, color: theme.primary }}>Configure Column Order for {table.display_name || table.name}</h3>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '1.5rem', cursor: 'pointer', color: theme.secondary, fontWeight: 500 }}>&times;</button>
                </div>

                <p style={{ fontSize: '0.9rem', color: theme.secondary, marginBottom: '1rem', fontWeight: 500 }}>
                    Drag and drop fields to change their display order.
                </p>

                <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1.5rem', border: `1px solid ${theme.primary}20`, borderRadius: '8px', padding: '0.5rem' }}>
                    {fields.map((field, index) => (
                        <div
                            key={field.name}
                            draggable
                            onDragStart={(e) => onDragStart(e, index)}
                            onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
                            onDragEnd={onDragEnd}
                            style={{
                                padding: '12px 16px',
                                marginBottom: '8px',
                                background: draggedItemIndex === index ? `${theme.primary}10` : '#fff',
                                border: `1px solid ${draggedItemIndex === index ? theme.primary : '#eee'}`,
                                borderRadius: '6px',
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'all 0.2s ease',
                                opacity: draggedItemIndex === index ? 0.6 : 1,
                            }}
                        >
                            <i className="bi bi-grip-vertical" style={{ color: '#ccc', fontSize: '1.2rem' }}></i>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600 }}>{field.display_name || field.name}</div>
                                <div style={{ fontSize: '0.8rem', color: theme.secondary, fontFamily: 'monospace', fontWeight: 500 }}>{field.name}</div>
                            </div>
                            <span className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>{field.data_type}</span>
                        </div>
                    ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button onClick={onClose} disabled={saving} className="btn" style={{ border: `1px solid ${theme.primary}`, color: theme.primary }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="btn" style={{ backgroundColor: theme.primary, color: '#fff' }}>
                        {saving ? <><i className="bi bi-arrow-repeat animate-spin me-2"></i>Saving...</> : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ColumnOrderModal;
